// Note: no React hooks used here

const SLIDE_BASE_WIDTH = 1280;
const SLIDE_BASE_HEIGHT = 720;

const parseVisualIdFromUrl = (src) => {
  if (!src?.includes('vis.snorkle')) return null;
  let id = null;
  const patterns = [
    /vis\.snorkle\.io\/([^/?#]+)\.html/,
    /vis\.snorkle\.io\/([^/?#]+)\/viewer\.html/,
    /vis\.snorkle\.io\/([^/?#.]+)/,
    /vis\.snorkle\.io.*[?&]id=([^&]+)/
  ];
  for (const pattern of patterns) {
    const match = src.match(pattern);
    if (match) {
      id = match[1].replace(/\/$/, '');
      break;
    }
  }
  return id;
};

// Helper to get QR URL from visual cache - shared between components
export const getQrUrl = (src, visCache) => {
  const id = parseVisualIdFromUrl(src);
  if (!id) return null;
  
  const visual = visCache?.[id];
  const qrUrl = visual?.qr_image || visual?.qr_url;
  
  return qrUrl;
};

// Update generateSlideHtml to accept an isForThumbnail flag
export const generateSlideHtml = (slideState, visCache, isForThumbnail = false, forceShowPopup = false) => {
  let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        html, body {
          margin: 0;
          padding: 0;
          width: 100%;
          height: 100%;
          background: #f0f0f0;
          overflow: hidden;
        }
        .wrapper {
          position: absolute;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .aspect-ratio {
          width: 100vw;
          height: 56.25vw; /* maintain 16:9 aspect ratio */
          max-height: 100vh;
          max-width: 177.78vh; /* maintain 16:9 aspect ratio */
          margin: auto;
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
        }
        .slide {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          ${typeof slideState.background === 'object' && slideState.background?.type === 'image'
            ? `background: url('${slideState.background.url}') center/cover no-repeat;`
            : `background: ${slideState.background};`}
        }
        .content {
          position: absolute;
          width: ${SLIDE_BASE_WIDTH}px;
          height: ${SLIDE_BASE_HEIGHT}px;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%) scale(var(--scale));
          transform-origin: center center;
        }
        .popup-overlay {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          z-index: 1000;
          align-items: center;
          justify-content: center;
        }
        .popup-content {
          background: white;
          padding: 30px;
          border-radius: 8px;
          width: 500px;
          max-width: 90%;
          max-height: 90vh;
          overflow: auto;
          position: relative;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          font-family: ${slideState.popupFont || 'Arial'}, sans-serif;
        }
        .popup-title {
          font-size: 24px;
          margin-bottom: 15px;
          line-height: 1.3;
          padding-right: 30px;
          font-family: inherit;
          padding-bottom: 15px;
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        }
        .popup-text {
          font-size: 16px;
          line-height: 1.6;
          white-space: pre-wrap;
          word-wrap: break-word;
          font-family: inherit;
          padding-top: 15px;
        }
        .popup-close {
          position: absolute;
          top: 15px;
          right: 15px;
          background: none;
          border: none;
          font-size: 24px;
          color: #666;
          cursor: pointer;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          border-radius: 50%;
          transition: background-color 0.2s;
          font-family: sans-serif;
        }
        .popup-close:hover {
          background-color: rgba(0, 0, 0, 0.05);
        }
      </style>
      <script>
        function updateScale() {
          const wrapper = document.querySelector('.wrapper');
          if (!wrapper) return;
          const content = document.querySelector('.content');
          if (!content) return;
          
          const scaleX = wrapper.clientWidth / ${SLIDE_BASE_WIDTH};
          const scaleY = wrapper.clientHeight / ${SLIDE_BASE_HEIGHT};
          const scale = Math.min(scaleX, scaleY);
          
          document.documentElement.style.setProperty('--scale', scale);
        }
        
        // Use a DOMContentLoaded listener for reliability with srcDoc
        document.addEventListener('DOMContentLoaded', function() {
          updateScale();
          window.addEventListener('resize', updateScale);
        });

        ${(forceShowPopup || !isForThumbnail) && slideState.showPopup ? `
        document.addEventListener('DOMContentLoaded', function() {
          const popup = document.querySelector('.popup-overlay');
          if(popup) {
            popup.style.display = 'flex';
            const closeButton = document.querySelector('.popup-close');
            if(closeButton) {
              closeButton.addEventListener('click', function() {
                popup.style.display = 'none';
              });
            }
          }
        });
        ` : ''}
      </script>
    </head>
    <body>
      ${(forceShowPopup || !isForThumbnail) && slideState.showPopup ? `
      <div class="popup-overlay" id="popupOverlay" style="display: flex;">
        <div class="popup-content">
          <button class="popup-close" onclick="document.getElementById('popupOverlay').style.display='none';">&times;</button>
          ${slideState.popupTitle ? `<div class="popup-title">${slideState.popupTitle}</div>` : ''}
          <div class="popup-text">${slideState.popupText}</div>
        </div>
      </div>
      ` : ''}
      <div class="wrapper">
        <div class="aspect-ratio">
          <div class="slide">
            <div class="content">
  `;

  // Unified render order based on stackOrder (newest on top)
  const typeOrder = { image: 0, iframe: 1, textbox: 2 };
  const merged = [
    ...(slideState.images || []).map((b, i) => ({ kind: 'image', box: b, idx: i })),
    ...(slideState.iframes || []).map((b, i) => ({ kind: 'iframe', box: b, idx: i })),
    ...(slideState.textboxes || []).map((b, i) => ({ kind: 'textbox', box: b, idx: i })),
  ].sort((a, b) => {
    const as = a.box.stackOrder || 0;
    const bs = b.box.stackOrder || 0;
    if (as !== bs) return as - bs; // lower first, higher (newer) last/on top
    const at = typeOrder[a.kind] ?? 0;
    const bt = typeOrder[b.kind] ?? 0;
    if (at !== bt) return at - bt;
    return a.idx - b.idx;
  });

  merged.forEach(item => {
    if (item.kind === 'image') {
      const box = item.box;
      const styles = `
      position: absolute;
          left: ${box.x}px; 
          top: ${box.y}px; 
          width: ${box.width}px; 
          height: ${box.height}px;
      overflow: hidden;
    `;
      const imgStyles = `
      width: 100%;
      height: 100%;
      object-fit: contain;
      pointer-events: none;
    `;
      html += `<div style="${styles.replace(/\s\s+/g, ' ').trim()}"><img src="${box.src}" style="${imgStyles}" alt="" draggable="false" /></div>`;
      return;
    }

    if (item.kind === 'iframe') {
      const frame = item.box;
      const isSnorkleVisual = frame.src?.includes('vis.snorkle');
      if (isSnorkleVisual && isForThumbnail) {
        const visualId = parseVisualIdFromUrl(frame.src);
        const visual = visualId ? visCache?.[visualId] : null;
        const thumbnailUrl = visual?.thumbnail;
        if (thumbnailUrl) {
          html += `
            <div style="position: absolute; left: ${frame.x}px; top: ${frame.y}px; width: ${frame.width}px; height: ${frame.height}px;">
              <img src="${thumbnailUrl}" style="width: 100%; height: 100%; object-fit: cover;" alt="Visual Thumbnail" />
            </div>
          `;
        } else {
          html += `
            <div style="position: absolute; left: ${frame.x}px; top: ${frame.y}px; width: ${frame.width}px; height: ${frame.height}px; background-color: #e0e0e0; border: 1px dashed #ccc;"></div>
          `;
        }
      } else {
        const showQR = isSnorkleVisual && slideState.iframeSettings?.[frame.id]?.showQR;
        const qrUrl = showQR ? getQrUrl(frame.src, visCache) : null;
        html += `
          <div style="position: absolute; left: ${frame.x}px; top: ${frame.y}px; width: ${frame.width}px; height: ${frame.height}px;">
            <iframe src="${frame.src}" style="width: 100%; height: 100%; border: none;"></iframe>
            ${qrUrl ? `
              <div style="position: absolute; bottom: 10px; right: 10px; width: 100px; height: 100px; background: rgba(255, 255, 255, 0.9); border-radius: 4px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); overflow: hidden;">
                <img src="${qrUrl}" style="width: 100%; height: 100%; object-fit: contain; border-radius: 4px;" alt="QR Code" />
              </div>
            ` : ''}
            </div>
        `;
      }
      return;
    }

    // textbox
    const box = item.box;
    const leftPx = box.x;
    const topPx = box.y;
    const widthPx = box.width;
    const heightStyle = box.height === 'auto' ? 'height: auto;' : `height: ${box.height}px;`;
    const fontPx = box.fontSize === '' ? 24 : box.fontSize;
    const isLink = !!box.linkUrl;
    const computedColor = isLink ? '#0d6efd' : box.fontColor;
    const computedDecoration = isLink ? 'underline' : (box.isUnderline ? 'underline' : 'none');
    const styles = `
      position: absolute;
      left: ${leftPx}px; 
      top: ${topPx}px; 
      width: ${widthPx}px; 
      ${heightStyle}
      color: ${computedColor};
      font-family: ${box.fontFamily}, sans-serif;
      font-size: ${fontPx}px;
      font-weight: ${box.isBold ? 'bold' : 'normal'};
      font-style: ${box.isItalic ? 'italic' : 'normal'};
      text-decoration: ${computedDecoration};
      line-height: 1.2;
      white-space: pre-wrap;
      word-break: break-word;
    `;
    const inner = box.text.replace(/\n/g, '<br>');
    let normalizedLink = (box.linkUrl || '').trim();
    if (normalizedLink && !/^(https?:|mailto:|tel:)/i.test(normalizedLink)) {
      normalizedLink = `https://${normalizedLink.replace(/^\/+/, '')}`;
    }
    const innerHtml = (box.html && box.html.trim())
      ? box.html
      : (isLink
        ? `<a href="${normalizedLink}" target="_blank" rel="noopener noreferrer" style="color: #0d6efd; text-decoration: underline;">${inner}</a>`
        : inner);
    html += `<div class="box" style="${styles.replace(/\s\s+/g, ' ').trim()}">${innerHtml}</div>`;
  });

  html += `
      </div>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
  return html;
}; 